import { useEffect } from 'react';

import useSendbirdStateContext from '@sendbird/uikit-react/useSendbirdStateContext';
import UserEventHandler from '@sendbird/uikit-react/handlers/UserEventHandler';

import {
  ClientChatTypes,
  getClientTabCount,
  getTypeOfUser,
  setClientTabUnreadCount,
  setUnreadCount,
  TypeOfUser,
} from '@pharmaplan/common';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import audio from '../../../assets/audio/live_chat.mp3';

import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';

const uniqueId = 'uniqueId';

const useClientUnreadChats = () => {
  const dispatch = useAppDispatch();
  const tabCounts = useAppSelector(getClientTabCount);

  const userType = useAppSelector(getTypeOfUser);

  const store = useSendbirdStateContext();
  const sdk = store?.stores?.sdkStore?.sdk;
  const notificationSound = new Audio(audio);

  const handleProfessionalUnread = (totalUnread: number) => {
    sdk?.groupChannel
      ?.getUnreadItemCount({ customTypesFilter: [userType] })
      .then(({ groupChannelUnreadMessageCount = 0 }) => {
        dispatch(
          setClientTabUnreadCount({
            ...tabCounts,
            [ClientChatTypes.support]:
              totalUnread - groupChannelUnreadMessageCount,
            [ClientChatTypes.pharmacy]: groupChannelUnreadMessageCount,
          }),
        );
      });
  };

  const handlePharmacyUnread = async (totalUnread: number) => {
    try {
      const { groupChannelUnreadMessageCount: pharmacistCount = 0 } = await sdk?.groupChannel.getUnreadItemCount({
        customTypesFilter: [TypeOfUser.pharmacist],
      }) ?? {};
      const { groupChannelUnreadMessageCount: technicianCount = 0 } = await sdk?.groupChannel.getUnreadItemCount({
        customTypesFilter: [TypeOfUser.technician],
      }) ?? {};
      const { groupChannelUnreadMessageCount: studentCount = 0 } = await sdk?.groupChannel.getUnreadItemCount({
        customTypesFilter: [TypeOfUser.student],
      }) ?? {};

      dispatch(
        setClientTabUnreadCount({
          [ClientChatTypes.pharmacist]: pharmacistCount,
          [ClientChatTypes.student]: studentCount,
          [ClientChatTypes.technician]: technicianCount,
          [ClientChatTypes.support]:
            totalUnread - technicianCount - studentCount - pharmacistCount,
        }),
      );
    } catch (err) {
      console.log(err);
    }
  };

  const retrieveUnreadCount = () => {
    sdk.groupChannel
      ?.getTotalUnreadMessageCount()
      .then((unread) => {
        dispatch(setUnreadCount(unread));

        if (userType === TypeOfUser.pharmacy) {
          handlePharmacyUnread(unread);
        } else {
          handleProfessionalUnread(unread);
        }
      });
  };

  useEffect(() => {
    const handler = new UserEventHandler({
      onTotalUnreadMessageCountChanged: (unread) => {
        const { groupChannelCount } = unread ?? {};
        dispatch(setUnreadCount(groupChannelCount));

        if (userType === TypeOfUser.pharmacy) {
          handlePharmacyUnread(groupChannelCount);
        } else {
          handleProfessionalUnread(groupChannelCount);
        }

        if (unread.groupChannelCount) {
          notificationSound.play();
        }
      },
    });

    sdk?.addUserEventHandler?.(uniqueId, handler);
    retrieveUnreadCount();

    return () => {
      sdk?.removeUserEventHandler?.(uniqueId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdk]);
};

export default useClientUnreadChats;
